import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

const PrintsFormInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
  } = useProductSelectionStore();

  const paperTypeInput = form?.inputs?.paperType;
  const printSizeInput = form?.inputs?.printSize;
  const paperTypeSelectedValue = paperTypeInput?.selectedValue;

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (
      paperTypeSelectedValue &&
      paperTypeSelectedValue !== 'default' &&
      printSizesQuery.isLoading
    ) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  // Keep requiredInputs in sync with current selections. Re-asserts the correct
  // state after store actions (e.g. processPrintSizeData) reset meta to defaults.
  useEffect(() => {
    const paperSelected =
      paperTypeSelectedValue && paperTypeSelectedValue !== 'default';
    const sizeSelected =
      printSizeInput?.selectedValue && printSizeInput.selectedValue !== 'default';

    const required = [];
    if (!paperSelected) required.push('Paper Type');
    if (!sizeSelected) required.push('Print Size');

    const current = form?.meta?.requiredInputs || [];
    const matches =
      current.length === required.length &&
      current.every((v, i) => v === required[i]);
    if (matches) return;

    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, requiredInputs: required },
    }));
  }, [
    paperTypeSelectedValue,
    printSizeInput?.selectedValue,
    form?.meta?.requiredInputs,
    setForm,
  ]);

  const handlePaperTypeChange = (e) => {
    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;

    setForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        paperType: {
          ...prevState.inputs.paperType,
          selectedValue: value,
          selectedValueText: selectedText,
        },
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: undefined,
          selectedValueText: undefined,
          selectedValuePrice: undefined,
        },
      },
    }));
  };

  const handlePrintSizeChange = (e) => {
    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedOption = (printSizeInput?.options || []).find(
      (option) => option?.value?.toString() === value,
    );

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        xAspectRatio: selectedOption?.xAspectRatio,
        yAspectRatio: selectedOption?.yAspectRatio,
        aspectRatio:
          selectedOption?.xAspectRatio && selectedOption?.yAspectRatio
            ? selectedOption.xAspectRatio / selectedOption.yAspectRatio
            : undefined,
      },
      inputs: {
        ...prevState.inputs,
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: value,
          selectedValueText: selectedText,
          selectedValuePrice: selectedOption?.price,
        },
      },
    }));
  };

  if (!form?.inputs) return null;

  const filterPaperOption = (option) =>
    option?.text && !option.text.toLowerCase().includes('canvas');

  const stripPrice = (text) => text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">{paperTypeInput?.label || 'Paper type'}</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePaperTypeChange}
              value={paperTypeSelectedValue || 'default'}
            >
              {paperTypeInput?.isLoading && (paperTypeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
              ) : (
                <option value="default">Choose One</option>
              )}
              {(paperTypeInput?.options || [])
                .filter(filterPaperOption)
                .filter((option) => !option.text?.endsWith('**'))
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {stripPrice(option.text)}
                  </option>
                ))}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>

      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">{printSizeInput?.label || 'Print size'}</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePrintSizeChange}
              value={printSizeInput?.selectedValue || 'default'}
              disabled={printSizeInput?.isDisabled}
            >
              {printSizeInput?.isLoading && (printSizeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
              ) : (
                <option value="default">Choose One</option>
              )}
              {(printSizeInput?.options || [])
                .filter((option) => !option.text?.endsWith('**'))
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default PrintsFormInputs;
